import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Users, Plus, Search, Trash2, RotateCcw, ExternalLink, 
  Copy, Check, Mail, Phone, Calendar, UserCheck, UserX,
  KeyRound, Eye, EyeOff, X, Edit2, Hash
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClientes() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchClients();
  }, [search]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  });

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_URL}/api/clients/?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewClientDetails = async (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    
    try {
      const response = await fetch(`${API_URL}/api/clients/${client.id}/admin-details`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClientDetails(data);
      } else {
        setClientDetails({
          client: client,
          reservations: [],
          events: [],
          experiences: []
        });
      }
    } catch (err) {
      setClientDetails({
        client: client,
        reservations: [],
        events: [],
        experiences: []
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleResetPin = async (clientId, clientName) => {
    if (!window.confirm(`¿Resetear el PIN de ${clientName} a 9999?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/reset-pin`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al resetear PIN');
      }

      toast.success(`PIN reseteado a: ${data.default_pin}`);
      fetchClients();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`¿Eliminar al cliente ${clientName}? Esta acción no se puede deshacer.`)) return;

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al eliminar');
      }

      toast.success('Cliente eliminado');
      fetchClients();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openClientPortal = (nickname) => {
    window.open(`/clientes/${nickname}`, '_blank');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-orange-400" />
            Gestión de Clientes
          </h1>
          <p className="text-slate-400 mt-1">
            Usuarios finales del portal de reservaciones
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Cliente
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por nombre, email o nickname..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
        />
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-300 font-medium">PIN por defecto: 9999</p>
            <p className="text-blue-300/70 text-sm">
              Los clientes deben cambiar su PIN en el primer inicio de sesión.
              El portal está disponible en <code className="bg-blue-500/20 px-1 rounded">/clientes/[nickname]</code>
            </p>
          </div>
        </div>
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-2xl border border-slate-700/50">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <h3 className="text-slate-400 font-medium mb-1">Sin clientes</h3>
          <p className="text-slate-500 text-sm">Crea el primer cliente para comenzar</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {clients.map((client) => (
            <div
              key={client.id}
              className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Client Info */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {client.avatar_url ? (
                      <img src={client.avatar_url} alt="" className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      client.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{client.name}</h3>
                      {client.is_active ? (
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Activo</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Inactivo</span>
                      )}
                      {!client.pin_changed && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">PIN pendiente</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Hash className="w-3.5 h-3.5" />
                        @{client.nickname}
                      </span>
                      <button
                        onClick={() => copyToClipboard(client.nickname, `nick-${client.id}`)}
                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                      >
                        {copiedId === `nick-${client.id}` ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {client.email}
                      </span>
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {client.phone}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-600">
                      <span>Reservaciones: {client.total_reservations || 0}</span>
                      {client.last_login && (
                        <span>Último acceso: {new Date(client.last_login).toLocaleDateString('es-MX')}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => viewClientDetails(client)}
                    className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
                    title="Ver detalles"
                  >
                    <Eye className="w-4 h-4 text-blue-400" />
                  </button>
                  <button
                    onClick={() => openClientPortal(client.nickname)}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                    title="Abrir portal"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-300" />
                  </button>
                  <button
                    onClick={() => handleResetPin(client.id, client.name)}
                    className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg transition-colors"
                    title="Resetear PIN"
                  >
                    <RotateCcw className="w-4 h-4 text-yellow-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(client.id, client.name)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateClientModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchClients();
          setShowCreateModal(false);
        }}
      />

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Detalles del Cliente</h2>
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            {loadingDetails ? (
              <div className="p-12 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
              </div>
            ) : selectedClient && (
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                    {selectedClient.avatar_url ? (
                      <img src={selectedClient.avatar_url} alt="" className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      selectedClient.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white">{selectedClient.name}</h3>
                    <p className="text-orange-400 font-medium">@{selectedClient.nickname}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {selectedClient.email}
                      </span>
                      {selectedClient.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" /> {selectedClient.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedClient.total_reservations || 0}</p>
                    <p className="text-xs text-slate-400">Reservaciones</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedClient.total_events || 0}</p>
                    <p className="text-xs text-slate-400">Eventos</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedClient.total_experiences || 0}</p>
                    <p className="text-xs text-slate-400">Experiencias</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{selectedClient.no_show_count || 0}</p>
                    <p className="text-xs text-slate-400">No-Shows</p>
                  </div>
                </div>

                {/* Account Details */}
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-3">Detalles de Cuenta</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">ID:</span>
                      <code className="text-slate-300 bg-slate-700 px-2 py-0.5 rounded text-xs">{selectedClient.id}</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Estado:</span>
                      <span className={selectedClient.is_active ? 'text-emerald-400' : 'text-red-400'}>
                        {selectedClient.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">PIN cambiado:</span>
                      <span className={selectedClient.pin_changed ? 'text-emerald-400' : 'text-yellow-400'}>
                        {selectedClient.pin_changed ? 'Sí' : 'No (pendiente)'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cumpleaños:</span>
                      <span className="text-slate-300">
                        {selectedClient.birthday || 'No especificado'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Creado:</span>
                      <span className="text-slate-300">
                        {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString('es-MX') : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Último acceso:</span>
                      <span className="text-slate-300">
                        {selectedClient.last_login ? new Date(selectedClient.last_login).toLocaleDateString('es-MX') : 'Nunca'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                {selectedClient.preferences && (
                  <div className="bg-slate-700/30 rounded-xl p-4">
                    <h4 className="font-semibold text-white mb-3">Preferencias</h4>
                    <div className="text-sm text-slate-300">
                      <p>Alergias: {selectedClient.preferences.allergies || 'Ninguna'}</p>
                      <p>Dieta: {selectedClient.preferences.dietary || 'Sin especificar'}</p>
                      <p>Notas: {selectedClient.preferences.special_notes || 'Sin notas'}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
                  <button
                    onClick={() => {
                      handleResetPin(selectedClient.id, selectedClient.name);
                    }}
                    className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-xl flex items-center gap-2 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" /> Resetear PIN
                  </button>
                  <button
                    onClick={() => openClientPortal(selectedClient.nickname)}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl flex items-center gap-2 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir Portal
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Create Client Modal
function CreateClientModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    whatsapp: '',
    nickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [nicknameAvailable, setNicknameAvailable] = useState(null);
  const [checkingNickname, setCheckingNickname] = useState(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  });

  // Check nickname availability
  useEffect(() => {
    if (formData.nickname.length >= 3) {
      const timeout = setTimeout(async () => {
        setCheckingNickname(true);
        try {
          const response = await fetch(`${API_URL}/api/clients/check-nickname/${formData.nickname}`);
          const data = await response.json();
          setNicknameAvailable(data.available);
        } catch (err) {
          console.error('Error checking nickname:', err);
        } finally {
          setCheckingNickname(false);
        }
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setNicknameAvailable(null);
    }
  }, [formData.nickname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.nickname) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    if (!nicknameAvailable) {
      toast.error('El nickname no está disponible');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al crear cliente');
      }

      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ first_name: '', last_name: '', email: '', whatsapp: '', nickname: '' });
    setNicknameAvailable(null);
    onClose();
  };

  // Auto-generate nickname from first + last name
  const generateNickname = () => {
    const fullName = `${formData.first_name} ${formData.last_name}`.trim();
    if (fullName) {
      const base = fullName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 20);
      const suffix = Math.floor(Math.random() * 100);
      setFormData({ ...formData, nickname: `${base}${suffix}` });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 rounded-2xl border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Nuevo Cliente</h2>
          <button onClick={handleClose} className="p-2 hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* First Name + Last Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Nombre(s) *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="Juan"
                className="w-full px-3 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Apellido(s) *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Pérez"
                className="w-full px-3 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                required
              />
            </div>
          </div>

          {/* Email (optional) */}
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Email <span className="text-slate-500 text-xs">(opcional)</span></label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="juan@email.com"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* WhatsApp (optional) */}
          <div>
            <label className="text-slate-400 text-sm mb-1 block">No. WhatsApp <span className="text-slate-500 text-xs">(opcional)</span></label>
            <input
              type="tel"
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
              placeholder="+52 55 1234 5678"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* Nickname */}
          <div>
            <label className="text-slate-400 text-sm mb-1 block">OkUser / Nickname *</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">@</span>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="juan_perez"
                  pattern="^[a-z0-9_]+$"
                  className="w-full pl-8 pr-10 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                  required
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingNickname ? (
                    <div className="w-5 h-5 border-2 border-slate-500 border-t-slate-300 rounded-full animate-spin" />
                  ) : nicknameAvailable === true ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : nicknameAvailable === false ? (
                    <X className="w-5 h-5 text-red-400" />
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={generateNickname}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-xl text-slate-300 text-sm transition-colors"
              >
                Generar
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              URL del portal: /clientes/{formData.nickname || 'nickname'}
            </p>
          </div>

          {/* PIN Info */}
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="text-yellow-300 text-sm flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              PIN inicial: <span className="font-mono font-bold">9999</span>
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || nicknameAvailable === false}
            className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creando...' : 'Crear Cliente'}
          </button>
        </form>
      </div>
    </div>
  );
}
